import { useCallback, useEffect, useRef, useState } from 'react';
import resolveApiBase from '../../tradingCockpit/apiBase';

const API_BASE = resolveApiBase();
const API_KEY = process.env.REACT_APP_API_KEY;

const resolveUrl = (path) => {
    if (!API_BASE) return path;
    const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

export function todayIstDateString() {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
}

const EMPTY_TOTALS = { pnlGross: 0, tcaCost: 0, pnlNet: 0, closedCount: 0, openCount: 0, winCount: 0 };

export function useHistoricalTrades({
    strategyId,
    from,
    to,
    status = 'closed',
    universe = null,
    enabled = true,
}) {
    const [trades, setTrades] = useState([]);
    const [totals, setTotals] = useState(EMPTY_TOTALS);
    const [fromDate, setFromDate] = useState(from);
    const [toDate, setToDate] = useState(to);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const aliveRef = useRef(true);

    const fetchHistory = useCallback(async () => {
        if (!enabled || !strategyId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (from) params.set('from', from);
            if (to) params.set('to', to);
            if (universe && universe !== 'ALL') params.set('universe', universe);
            if (status) params.set('status', status);
            const path =
                strategyId === 'vyasa'
                    ? `/api/strategies/vyasa/history?${params}`
                    : `/api/strategies/${strategyId}/history?${params}`;
            const headers = { 'Content-Type': 'application/json' };
            if (API_KEY) headers['x-api-key'] = API_KEY;
            const resp = await fetch(resolveUrl(path), {
                method: 'GET',
                headers,
                credentials: 'include',
                cache: 'no-store',
            });
            if (!resp.ok) {
                throw new Error(`HTTP ${resp.status}`);
            }
            const data = await resp.json();
            if (!aliveRef.current) return;
            setTrades(Array.isArray(data.trades) ? data.trades : []);
            setTotals({ ...EMPTY_TOTALS, ...(data.totals || {}) });
            setFromDate(data.fromDate || from);
            setToDate(data.toDate || to);
            setError(null);
        } catch (err) {
            if (!aliveRef.current) return;
            setTrades([]);
            setTotals(EMPTY_TOTALS);
            setError(err.message || String(err));
        } finally {
            if (aliveRef.current) setLoading(false);
        }
    }, [enabled, strategyId, from, to, status, universe]);

    useEffect(() => {
        aliveRef.current = true;
        fetchHistory();
        return () => {
            aliveRef.current = false;
        };
    }, [fetchHistory]);

    return {
        trades,
        totals,
        fromDate,
        toDate,
        loading,
        error,
        refresh: fetchHistory,
    };
}

export default useHistoricalTrades;
